var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* eslint-disable @typescript-eslint/unbound-method */
import { Translatable } from "@bender/localization";
import { html, LitElement, svg, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { env } from "../auth/config/env";
import { styles } from "../styles";
import { BenderLocalizationLoader } from "../util/bender-translations";
import { invokeCoreApi, invokeCoreApiRaw } from "../util/coreApiClient";
/**
 * The bender-profile element. Consists of a profile button that toggles a dropdown menu that e.g. allows to logout.
 */
let Profile = class Profile extends LitElement {
    constructor() {
        super();
        this.showDropdown = false;
        this.isTranslationDataLoaded = false;
        this.showTenantSwitcher = false;
        this.tenantSearchQuery = "";
        this.allUserTenants = [];
        this.isFetchingTenants = false;
        this.showJoinTenants = false;
        this.joinTenantsSearchQuery = "";
        this.allSystemTenants = [];
        this.selectedTenantIds = new Set();
        this.isJoining = false;
        this.isFetchingJoinableTenants = false;
        this.joinStatusMessage = null;
        this.iconEnd = svg `
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.5 7.33789H9.66211V10.8379H0.337891V1.5H3.83789V2.66211H1.5V9.66211H8.5V7.33789ZM5.58789 0.912109V2.08789H9.08789V5.58789H10.25V0.912109H5.58789ZM9.67578 2.32031L8.8418 1.5L4.00195 6.33984L4.83594 7.16016L9.67578 2.32031Z" fill="#B5B5B5"/>
  </svg>
  `;
        this.unknowUserIcon = svg `
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 7.99219C6.89062 7.99219 5.94531 7.60156 5.16406 6.82031C4.38281 6.03906 3.99219 5.10156 3.99219 4.00781C3.99219 2.89844 4.38281 1.95313 5.16406 1.17188C5.94531 0.390625 6.89062 0 8 0C9.10938 0 10.0547 0.390625 10.8359 1.17188C11.6172 1.95313 12.0078 2.89844 12.0078 4.00781C12.0078 5.10156 11.6172 6.03906 10.8359 6.82031C10.0547 7.60156 9.10938 7.99219 8 7.99219ZM8 10.0078C8.67188 10.0078 9.46094 10.0859 10.3672 10.2422C11.2891 10.4141 12.1641 10.6719 12.9922 11.0156C13.8359 11.3438 14.5469 11.7578 15.125 12.2578C15.7031 12.7578 15.9922 13.3359 15.9922 13.9922V16.0078H0.0078125V13.9922C0.0078125 13.3359 0.296875 12.7578 0.875 12.2578C1.45312 11.7578 2.15625 11.3438 2.98438 11.0156C3.82812 10.6719 4.71094 10.4141 5.63281 10.2422C6.53906 10.0859 7.32812 10.0078 8 10.0078Z" fill="#3a3a3a"/>
  </svg>
  `;
        this.localizationLoader = new BenderLocalizationLoader();
        this.hasSingleTenant = false;
        this.firstClick = true;
        this.profileImage = "";
        this.initials = "";
        this.settingsOptions = [
            {
                text: "key:user_settings",
                fallBackText: "User settings",
                link: `${env.frontDoorUrl}/management/user`
            },
            {
                text: "key:tenant_settings",
                fallBackText: "Company account settings",
                link: `${env.frontDoorUrl}/management/company-account/overview`
            }
        ];
        this.dataProtectionOptions = [
            {
                text: "key:Common_imprint",
                fallBackText: "Imprint",
                link: "https://www.bender.de/en/imprint/"
            },
            {
                text: "key:data_protection",
                fallBackText: "Data Protection",
                get link() {
                    const language = localStorage.getItem("benderPreferredLanguage") || navigator.language;
                    const lang = language.toLowerCase();
                    return lang.startsWith("de")
                        ? "https://www.bender.de/datenschutzerklaerung/#:~:text=datenschutz%40bender.de.-,Bender%20Connect,-Um%20Bender%20Connect"
                        : "https://www.bender.de/en/data-protection-declaration/#:~:text=datenschutz%40bender.de.-,Bender%20Connect,-In%20order%20to";
                }
            }
        ];
        this.loadProfileImage = async () => {
            invokeCoreApiRaw(env.frontDoorUrl, "/profile-pictures/self", "get", {})
                .then(async (res) => {
                if (res.status === 200) {
                    this.profileImage = URL.createObjectURL(await res.blob());
                }
                else if (res.status === 204) {
                    this.profileImage = "";
                }
                else {
                    this.profileImage = "";
                }
                this.requestUpdate();
            })
                .catch(() => {
                this.profileImage = "";
                this.requestUpdate();
            });
        };
        this.loadTenant = async () => {
            invokeCoreApi(env.frontDoorUrl, "/tenants/self", "get", {})
                .then(async (tenant) => {
                this.tenant = tenant;
                this.requestUpdate();
            })
                .catch(err => {
                throw err;
            });
        };
    }
    connectedCallback() {
        super.connectedCallback();
        const bindedCheckHasAccount = this.checkHasAccount.bind(this);
        this.intervalLoginCheck = setInterval(bindedCheckHasAccount, 1000);
        void this.localizationLoader.loadLocalizations().then(localizationServiceAdapter => {
            this.translator = localizationServiceAdapter;
            this.isTranslationDataLoaded = true;
        });
        this.loginAndLoadProfileImage();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
    }
    /**
     * The toggle state of the profile dropdown.
     */
    render() {
        return html `
      <div class="profile-container">
        <button
          type="button"
          class="header-btn"
          @click=${async () => {
            await this.handleClick();
        }}
        >
          ${this.profileImageTemplate()}
        </button>
        <div
          ?hidden=${!this.showDropdown}
          class="dropdown-content"
        >
          ${this.dropdownTemplate()}
        </div>
      </div>
      <div
        ?hidden=${!this.showDropdown}
        @click=${() => {
            this.showDropdown = false;
        }}
        class="dropdown-background"
      ></div>
    `;
    }
    i18n(key, fallback) {
        return this.isTranslationDataLoaded ? Translatable.translate(key, this.translator) : fallback;
    }
    profileImageTemplate() {
        var _a;
        const isUserLoggedIn = window.benderAuth.hasAccounts;
        if (this.profileImage == "") {
            return isUserLoggedIn ? html ` <div class="profile-initials">${this.initials}</div> ` : html `${this.unknowUserIcon}`;
        }
        return html `
      <img
        src=${this.profileImage}
        class="profile-image"
        alt="${this.i18n("key:profile_picture", "Profile picture")} ${(_a = this.account) === null || _a === void 0 ? void 0 : _a.displayName}"
      />
    `;
    }
    optionsTemplate(options) {
        return options.map(option => {
            return html `
        <a
          class="dropdown-item"
          href="${option.link}"
        >
          ${this.i18n(option.text, option.fallBackText)} ${this.iconEnd}
        </a>
      `;
        });
    }
    dropdownTemplate() {
        var _a, _b, _c, _d, _e;
        const isUserLoggedIn = window.benderAuth.hasAccounts;
        if (isUserLoggedIn) {
            if (this.showJoinTenants) {
                return this.joinTenantsTemplate();
            }
            if (this.showTenantSwitcher) {
                return html `
          <div class="tenant-switcher-container">
            <input
              type="text"
              class="tenant-search-input"
              placeholder=${this.i18n("key:sysadmin_search_tenant", "Search company account...")}
              .value=${this.tenantSearchQuery}
              @input=${this.handleSearchInput}
              id="tenant-search-input"
            />

            ${this.isFetchingTenants
                    ? html `<div class="tenant-loading">${this.i18n("key:common_load", "Loading...")}</div>`
                    : html `
                    <div class="tenant-list">
                      ${this.filteredTenants().map(t => {
                        var _a, _b;
                        return html `
                          <div
                            class="tenant-list-item ${((_a = this.tenant) === null || _a === void 0 ? void 0 : _a.id) === t.id ? "tenant-list-item--current" : ""}"
                            @click=${() => this.selectTenant(t)}
                          >
                            <span class="tenant-item-name">${t.name}</span>
                            <span class="tenant-item-id">ID: ${t.connectId}</span>
                            ${((_b = this.tenant) === null || _b === void 0 ? void 0 : _b.id) === t.id
                            ? html `<span class="tenant-item-current-badge">${this.i18n("key:Ids_Description_072", "Current")}</span>`
                            : ""}
                          </div>
                        `;
                    })}
                      ${this.filteredTenants().length === 0
                        ? html `<div class="no-tenants-found">
                              ${this.i18n("key:could_not_load_company_accounts", "Could not load company accounts")}
                            </div>`
                        : ""}
                    </div>
                  `}

            <button
              type="button"
              class="btn btn-default btn-sm cancel-switch-btn"
              @click=${this.cancelTenantSwitch}
            >
              ${this.i18n("key:Tools_Common_Cancel", "Cancel")}
            </button>
          </div>
        `;
            }
            return html `
        <div class="profile-info-container">
          ${this.profileImageTemplate()}
          <div
            class="display-name"
            id="profile-displayname"
          >
            ${(_a = this.account) === null || _a === void 0 ? void 0 : _a.name} ${(_b = this.account) === null || _b === void 0 ? void 0 : _b.surname}
          </div>
          <div
            class="tenant-container"
            ?hidden="${this.tenant == null}"
          >
            <span class="tenant-name">
              ${(_c = this.tenant) === null || _c === void 0 ? void 0 : _c.name}
              <span
                id="connect-id"
                class="tooltip-tenant"
              >
                ID: ${(_d = this.tenant) === null || _d === void 0 ? void 0 : _d.connectId}
              </span>
            </span>
            <button
              class="btn btn-default btn-sm"
              id="switch-tenant-btn"
              ?hidden="${this.hasSingleTenant}"
              @click=${this.handleTenantSwitchClick}
            >
              ${this.i18n("key:switch_tenant", "Switch company account")}
            </button>
            ${((_e = this.account) === null || _e === void 0 ? void 0 : _e.rights.includes("TENANT_SELF_JOIN"))
                ? html `
                    <button
                      class="btn btn-default btn-sm"
                      id="join-tenants-btn"
                      @click=${this.handleJoinTenantsClick}
                    >
                      ${this.i18n("key:join_company_accounts", "Join company accounts")}
                    </button>
                  `
                : ""}
          </div>
        </div>
        <div class="dropdown-content-list">
          <hr class="divider" />
          ${this.optionsTemplate(this.filteredSettingsOptions)}
          <hr class="divider" />
          ${this.optionsTemplate(this.dataProtectionOptions)}
          <hr class="divider" />
          <a
            class="dropdown-item"
            id="logout-btn"
            @click=${this.handleLogout}
          >
            ${this.i18n("key:Tools_Logout", "Log out")}
          </a>
        </div>
      `;
        }
        return html ``;
    }
    async handleClick() {
        if (window.benderAuth.hasAccounts === false) {
            await window.benderAuth.login();
        }
        else {
            // load profile information only on the first click
            if (this.firstClick) {
                this.firstClick = false;
                void this.loadTenant();
            }
            this.toggleDropdown();
        }
    }
    async loginAndLoadProfileImage() {
        var _a, _b, _c, _d, _e;
        this.account = window.benderAuth.getAccount();
        this.initials = ((_b = (_a = this.account) === null || _a === void 0 ? void 0 : _a.name) === null || _b === void 0 ? void 0 : _b.charAt(0).toUpperCase()) + ((_d = (_c = this.account) === null || _c === void 0 ? void 0 : _c.surname) === null || _d === void 0 ? void 0 : _d.charAt(0).toUpperCase());
        this.hasSingleTenant = !((_e = this.account) === null || _e === void 0 ? void 0 : _e.hasMultipleTenants);
        await this.loadProfileImage();
        void this.loadTenant();
    }
    get filteredSettingsOptions() {
        var _a, _b;
        const rights = (_b = (_a = this.account) === null || _a === void 0 ? void 0 : _a.rights) !== null && _b !== void 0 ? _b : [];
        return this.settingsOptions.filter(o => !o.requiredRight || rights.includes(o.requiredRight));
    }
    checkHasAccount() {
        if (window.benderAuth.hasAccounts) {
            clearInterval(this.intervalLoginCheck);
            this.loginAndLoadProfileImage();
        }
    }
    async handleLogout() {
        await window.benderAuth.logout();
    }
    joinTenantsTemplate() {
        const filtered = this.filteredJoinableTenants();
        return html `
      <div class="join-tenants-container">
        <input
          type="text"
          class="tenant-search-input"
          placeholder=${this.i18n("key:sysadmin_search_tenant", "Search company account...")}
          .value=${this.joinTenantsSearchQuery}
          @input=${this.handleJoinSearchInput}
        />

        ${this.isFetchingJoinableTenants
            ? html `<div class="tenant-loading">${this.i18n("key:common_load", "Loading...")}</div>`
            : html `
                <div class="join-tenants-list">
                  ${filtered.map(t => html `
                      <div
                        class="join-tenants-item ${this.selectedTenantIds.has(t.id) ? "join-tenants-item--selected" : ""}"
                        @click=${() => this.toggleTenantSelection(t.id)}
                      >
                        <span class="join-tenants-checkmark">${this.selectedTenantIds.has(t.id) ? "✓" : ""}</span>
                        <span>
                          <span class="tenant-item-name">${t.name}</span>
                          <span class="tenant-item-id">ID: ${t.connectId}</span>
                        </span>
                      </div>
                    `)}
                  ${filtered.length === 0
                ? html `<div class="no-tenants-found">
                          ${this.i18n("key:could_not_load_company_accounts", "Could not load company accounts")}
                        </div>`
                : ""}
                </div>
              `}
        ${this.selectedTenantIds.size > 0
            ? html `<span class="join-tenants-selected-count"
                >${this.selectedTenantIds.size} ${this.i18n("key:company_accounts_selected", "company account(s) selected")}</span
              >`
            : ""}
        ${this.joinStatusMessage
            ? html `<div class="join-tenants-status join-tenants-status--${this.joinStatusMessage.type}">
                ${this.joinStatusMessage.text}
              </div>`
            : ""}

        <div class="join-tenants-actions">
          <button
            type="button"
            class="btn btn-default btn-sm cancel-switch-btn"
            @click=${this.cancelJoinTenants}
          >
            ${this.i18n("key:Tools_Common_Cancel", "Cancel")}
          </button>
          <button
            type="button"
            class="join-tenants-submit-btn"
            ?disabled=${this.selectedTenantIds.size === 0 || this.isJoining}
            @click=${this.joinSelectedTenants}
          >
            ${this.isJoining
            ? this.i18n("key:joining", "Joining...")
            : `${this.i18n("key:join_selected", "Join selected")} (${this.selectedTenantIds.size})`}
          </button>
        </div>
      </div>
    `;
    }
    async handleJoinTenantsClick() {
        this.showJoinTenants = true;
        void this.loadAllSystemTenants();
    }
    handleJoinSearchInput(e) {
        this.joinTenantsSearchQuery = e.target.value;
        this.requestUpdate();
    }
    filteredJoinableTenants() {
        const query = this.joinTenantsSearchQuery.toLowerCase().trim();
        if (!query) {
            return this.allSystemTenants;
        }
        return this.allSystemTenants.filter(t => t.name.toLowerCase().includes(query) || (t.connectId && t.connectId.toLowerCase().includes(query)));
    }
    async loadAllSystemTenants() {
        if (this.allSystemTenants.length > 0) {
            return;
        }
        this.isFetchingJoinableTenants = true;
        this.requestUpdate();
        try {
            const res = await invokeCoreApi(env.frontDoorUrl, "/tenants/all", "get", {});
            this.allSystemTenants = res.tenants.map(t => { var _a; return ({ id: t.id, name: t.name, connectId: (_a = t.connectId) !== null && _a !== void 0 ? _a : "" }); });
        }
        catch {
            this.allSystemTenants = [];
        }
        this.isFetchingJoinableTenants = false;
        this.requestUpdate();
    }
    toggleTenantSelection(id) {
        const updated = new Set(this.selectedTenantIds);
        if (updated.has(id)) {
            updated.delete(id);
        }
        else {
            updated.add(id);
        }
        this.selectedTenantIds = updated;
    }
    async joinSelectedTenants() {
        if (this.selectedTenantIds.size === 0 || this.isJoining) {
            return;
        }
        this.isJoining = true;
        this.joinStatusMessage = null;
        this.requestUpdate();
        try {
            const token = await window.benderAuth.getToken();
            const response = await fetch(`${env.frontDoorUrl}/api/core/users/self/tenants/join-bulk`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ tenantIds: [...this.selectedTenantIds] })
            });
            const result = (await response.json());
            const joined = result.joinedTenantIds.length;
            const failed = result.failedTenantIds.length;
            if (failed === 0) {
                this.joinStatusMessage = {
                    text: `${this.i18n("key:company_accounts_joined_success", "Company accounts successfully joined")}: ${joined}`,
                    type: "success"
                };
            }
            else {
                this.joinStatusMessage = {
                    text: `${this.i18n("key:company_accounts_joined_success", "Company accounts joined")}: ${joined}, ${this.i18n("key:failed", "failed")}: ${failed}`,
                    type: "warning"
                };
            }
            this.selectedTenantIds = new Set();
        }
        catch {
            this.joinStatusMessage = {
                text: this.i18n("key:joining_company_accounts_failed", "Failed to join company accounts. Please try again."),
                type: "error"
            };
        }
        this.isJoining = false;
        this.requestUpdate();
        setTimeout(() => {
            this.joinStatusMessage = null;
            this.requestUpdate();
        }, 4000);
    }
    cancelJoinTenants() {
        this.showJoinTenants = false;
        this.joinTenantsSearchQuery = "";
        this.selectedTenantIds = new Set();
        this.joinStatusMessage = null;
        this.requestUpdate();
    }
    async handleTenantSwitchClick() {
        this.showTenantSwitcher = true;
        void this.loadAllUserTenants();
    }
    handleSearchInput(e) {
        this.tenantSearchQuery = e.target.value;
        this.requestUpdate();
    }
    filteredTenants() {
        const query = this.tenantSearchQuery.toLowerCase().trim();
        if (!query) {
            return this.allUserTenants;
        }
        return this.allUserTenants.filter(t => t.name.toLowerCase().includes(query) || (t.connectId && t.connectId.toLowerCase().includes(query)));
    }
    async loadAllUserTenants() {
        if (this.allUserTenants.length > 0) {
            return;
        }
        this.isFetchingTenants = true;
        this.requestUpdate();
        try {
            const res = await invokeCoreApi(env.frontDoorUrl, "/users/{userId}/tenants", "get", { parameters: { userId: this.account.userId } });
            this.allUserTenants = res.tenants;
        }
        catch {
            this.allUserTenants = [];
        }
        this.isFetchingTenants = false;
        this.requestUpdate();
    }
    async selectTenant(tenant) {
        if (this.tenant && this.tenant.id === tenant.id) {
            this.cancelTenantSwitch();
            return;
        }
        this.isFetchingTenants = true;
        this.requestUpdate();
        try {
            await window.benderAuth.switchTenantSilent(tenant.id);
            // Token for the new tenant is now in the MSAL localStorage cache.
            // A full reload re-fetches all app data under the new tenant without a B2C round-trip.
            window.location.reload();
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.warn("Silent tenant switch failed, falling back to interactive switch", err);
            await window.benderAuth.switchTenant(tenant.id);
        }
        finally {
            this.isFetchingTenants = false;
            this.requestUpdate();
        }
    }
    cancelTenantSwitch() {
        this.showTenantSwitcher = false;
        this.tenantSearchQuery = "";
        this.requestUpdate();
    }
    toggleDropdown() {
        this.showDropdown = !this.showDropdown;
        if (!this.showDropdown) {
            this.showTenantSwitcher = false;
            this.tenantSearchQuery = "";
            this.showJoinTenants = false;
            this.joinTenantsSearchQuery = "";
            this.selectedTenantIds = new Set();
            this.joinStatusMessage = null;
        }
    }
};
Profile.styles = [
    styles,
    css `
      .join-tenants-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 12px;
        width: 260px;
        box-sizing: border-box;
      }
      .join-tenants-list {
        max-height: 180px;
        overflow-y: auto;
        border: 1px solid var(--bender-grey-9, #ebebeb);
        border-radius: var(--border-radius, 3px);
        background: var(--bender-white, #ffffff);
      }
      .join-tenants-item {
        padding: 8px 12px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: background-color 0.15s ease;
        border-bottom: 1px solid var(--bender-grey-10, #f6f6f6);
      }
      .join-tenants-item:last-child {
        border-bottom: none;
      }
      .join-tenants-item:hover {
        background-color: var(--bender-grey-10, #f6f6f6);
      }
      .join-tenants-item--selected {
        background-color: var(--bender-yellow-plus-2, #fff9e6);
      }
      .join-tenants-checkmark {
        width: 14px;
        height: 14px;
        border: 1px solid var(--bender-grey-6, #c4c4c4);
        border-radius: 2px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.6rem;
        flex-shrink: 0;
        background: var(--bender-white, #ffffff);
      }
      .join-tenants-item--selected .join-tenants-checkmark {
        background: var(--custom-primary-color, var(--bender-yellow-minus-1, #e2ab00));
        border-color: var(--custom-primary-color, var(--bender-yellow-minus-1, #e2ab00));
        color: var(--bender-white, #ffffff);
      }
      .join-tenants-selected-count {
        font-size: 0.75rem;
        color: var(--bender-grey-4, #898989);
      }
      .join-tenants-actions {
        display: flex;
        justify-content: space-between;
        gap: 6px;
      }
      .join-tenants-submit-btn {
        flex: 1;
        background: var(--custom-primary-color, var(--bender-yellow-minus-1, #e2ab00));
        color: var(--bender-white, #ffffff);
        border: none;
        border-radius: var(--border-radius, 3px);
        padding: 6px 10px;
        font-size: 0.8125rem;
        cursor: pointer;
        font-family: var(--font-family);
      }
      .join-tenants-submit-btn:disabled {
        opacity: 0.5;
        cursor: default;
      }
      .join-tenants-status {
        font-size: 0.75rem;
        text-align: center;
        padding: 4px 0;
        border-radius: var(--border-radius, 3px);
      }
      .join-tenants-status--success {
        color: #2e7d32;
        background: #e8f5e9;
      }
      .join-tenants-status--warning {
        color: #e65100;
        background: #fff3e0;
      }
      .join-tenants-status--error {
        color: #c62828;
        background: #ffebee;
      }
      .tenant-switcher-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 12px;
        width: 260px;
        box-sizing: border-box;
      }
      .tenant-search-input {
        box-sizing: border-box;
        width: 100%;
        padding: 8px 12px;
        border: 1px solid var(--bender-grey-8, #e1e1e1);
        border-radius: var(--border-radius, 3px);
        font-family: var(--font-family);
        font-size: 0.875rem;
        outline: none;
      }
      .tenant-search-input:focus {
        border-color: var(--custom-primary-color, var(--bender-yellow-minus-1, #e2ab00));
      }
      .tenant-list {
        max-height: 180px;
        overflow-y: auto;
        border: 1px solid var(--bender-grey-9, #ebebeb);
        border-radius: var(--border-radius, 3px);
        background: var(--bender-white, #ffffff);
      }
      .tenant-list-item {
        padding: 8px 12px;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        transition: background-color 0.15s ease;
        border-bottom: 1px solid var(--bender-grey-10, #f6f6f6);
        text-align: left;
      }
      .tenant-list-item:last-child {
        border-bottom: none;
      }
      .tenant-list-item:hover {
        background-color: var(--bender-grey-10, #f6f6f6);
      }
      .tenant-list-item--current {
        opacity: 0.5;
        cursor: default;
        pointer-events: none;
      }
      .tenant-item-current-badge {
        font-size: 0.625rem;
        color: var(--bender-grey-4, #898989);
        margin-top: 2px;
        font-style: italic;
      }
      .tenant-item-name {
        font-weight: 600;
        color: var(--bender-grey-0, #3a3a3a);
        font-size: 0.8125rem;
      }
      .tenant-item-id {
        font-size: 0.6875rem;
        color: var(--bender-grey-4, #898989);
        margin-top: 2px;
      }
      .no-tenants-found {
        padding: 12px;
        text-align: center;
        font-size: 0.8125rem;
        color: var(--bender-grey-4, #898989);
      }
      .cancel-switch-btn {
        align-self: flex-end;
      }
      .tenant-loading {
        font-size: 0.8125rem;
        color: var(--bender-grey-4, #898989);
        text-align: center;
        padding: 12px;
      }
    `
];
__decorate([
    state()
], Profile.prototype, "showDropdown", void 0);
__decorate([
    state()
], Profile.prototype, "isTranslationDataLoaded", void 0);
__decorate([
    state()
], Profile.prototype, "translator", void 0);
__decorate([
    state()
], Profile.prototype, "showTenantSwitcher", void 0);
__decorate([
    state()
], Profile.prototype, "tenantSearchQuery", void 0);
__decorate([
    state()
], Profile.prototype, "allUserTenants", void 0);
__decorate([
    state()
], Profile.prototype, "isFetchingTenants", void 0);
__decorate([
    state()
], Profile.prototype, "showJoinTenants", void 0);
__decorate([
    state()
], Profile.prototype, "joinTenantsSearchQuery", void 0);
__decorate([
    state()
], Profile.prototype, "allSystemTenants", void 0);
__decorate([
    state()
], Profile.prototype, "selectedTenantIds", void 0);
__decorate([
    state()
], Profile.prototype, "isJoining", void 0);
__decorate([
    state()
], Profile.prototype, "isFetchingJoinableTenants", void 0);
__decorate([
    state()
], Profile.prototype, "joinStatusMessage", void 0);
Profile = __decorate([
    customElement("bender-profile")
], Profile);
export { Profile };
//# sourceMappingURL=profile.js.map